import { useState } from "preact/hooks"
import { useFetch } from "../hooks/useFetch"
import type { ActiveProfile, Me } from "../types"
import { LoadError } from "./LoadError"
import { fetchProfiles, switchProfile } from "./lib/api"
import { Modal } from "./Modal"

interface KidNavbarProps {
	me: Me
	// The profile this session is playing as. KidNavbar only renders in kid
	// mode, so this is never null.
	profile: ActiveProfile
	// Called once the switch has been made: App re-reads /api/auth/me and goes
	// back to "/", which is what actually swaps the whole shell.
	onProfileChanged: () => void
}

// The navbar a kid profile sees: who is playing, and the way back. No Home,
// Friends, username or Logout links — none of them lead anywhere a profile is
// allowed to go, and a five-year-old should not be able to log the family out.
//
// One row on purpose, and it has to hold at 375px: the logo is fixed, the
// name takes what is left (and truncates), and the button never wraps.
export function KidNavbar({ me, profile, onProfileChanged }: KidNavbarProps) {
	const [picking, setPicking] = useState(false)

	return (
		// flex-wrap stays off here (unlike Navbar's own wrapping row): this is
		// the one row a kid profile gets, and it has to hold at 375px — the
		// logo is fixed, the name takes what's left (and truncates), and the
		// button never wraps.
		<nav class="navbar" style="flex-wrap: nowrap">
			<a class="plain inline-flex center gap-2 mr-2" href="/">
				<img
					src="/static/brand/logo.svg"
					alt="Quizfish"
					width="28"
					height="28"
				/>
				<span class="brand-name hide-narrow">Quizfish</span>
			</a>
			<span class="grow truncate fw-semibold">{profile.name}</span>
			<button type="button" class="sm nowrap" onClick={() => setPicking(true)}>
				Switch profile
			</button>
			{/* Modal renders nothing while closed, so the picker mounts when the
			    dialog opens and goes away with it — which is what fetches the
			    list afresh each time. */}
			<Modal
				open={picking}
				onClose={() => setPicking(false)}
				title="Who's playing?"
			>
				<SwitchList
					me={me}
					currentId={profile.id}
					onProfileChanged={onProfileChanged}
				/>
			</Modal>
		</nav>
	)
}

interface SwitchListProps {
	me: Me
	currentId: number
	onProfileChanged: () => void
}

// The picker: the grown-up first, then every profile. The list is fetched
// each time the dialog opens (it is one small request, and a profile added on
// another device should show up here without a reload).
function SwitchList({ me, currentId, onProfileChanged }: SwitchListProps) {
	const {
		data: profiles,
		error: loadError,
		reload: load,
	} = useFetch(fetchProfiles, "Couldn't load the profiles.")
	// Set while a switch is in flight, and after it fails.
	const [switchError, setSwitchError] = useState("")
	const [busy, setBusy] = useState(false)

	async function pick(id: number | null) {
		setBusy(true)
		setSwitchError("")
		try {
			await switchProfile(id)
			onProfileChanged()
		} catch (e) {
			setSwitchError(e instanceof Error ? e.message : "Couldn't switch.")
			setBusy(false)
		}
	}

	return (
		<div class="stack gap-3">
			{switchError && <div class="alert danger">{switchError}</div>}
			<div class="list">
				{/* The owner is always offered, and always reachable: this is the
				    way out of a kid profile, so it must not depend on the list
				    below having loaded. */}
				<button
					type="button"
					class="flex between center gap-2"
					disabled={busy}
					onClick={() => pick(null)}
				>
					<span class="fw-semibold">{me.username}</span>
					<span class="muted small">grown-up</span>
				</button>
				{profiles?.map((p) => (
					<button
						type="button"
						key={p.id}
						class="flex between center gap-2"
						disabled={busy || p.id === currentId}
						onClick={() => pick(p.id)}
					>
						<span class="fw-semibold">{p.name}</span>
						{p.id === currentId && <span class="muted small">playing now</span>}
					</button>
				))}
			</div>
			{/* Three states for the fetched half: failed (with a retry), still
			    loading, and loaded-but-empty — never one standing in for
			    another. */}
			{loadError ? (
				<LoadError message={loadError} onRetry={load} />
			) : !profiles ? (
				<div class="spinner sm" role="status" />
			) : profiles.length === 0 ? (
				<p class="muted small">No profiles yet.</p>
			) : null}
		</div>
	)
}
